<template>
  <IntercomModal
    v-model:show="showModal"
    :title="isFinalizedEdit ? 'Edit Finalized Purchase Bill' : isEditMode ? 'Edit Purchase Bill' : 'New Purchase Bill'"
    width="900px"
  >
    <div class="purchase-bill-form" @keydown.escape="handleEscape">
      <!-- Vendor & Vendor Bill Number -->
      <div class="form-row">
        <div class="form-field" data-autofocus>
          <IntercomSelect
            ref="vendorSelectRef"
            v-model="formData.vendor_id"
            label="Vendor"
            :options="vendorSelectOptions"
            placeholder="Select Vendor"
            filterable
            :disabled="isLoadingVendors"
            @update:model-value="handleVendorSelectChange"
            @keydown.enter="onSelectEnter('vendor', $event)"
            @search="handleVendorSearch"
          >
            <template #option="{ option }">
              <span :class="{ 'add-new-option': option.isCreateNew }">{{ option.label }}</span>
            </template>
          </IntercomSelect>

          <!-- Inline New Vendor Form -->
          <div v-if="showNewVendorForm" class="new-vendor-form">
            <div class="new-vendor-header">
              <span class="new-vendor-title">New Vendor</span>
            </div>
            <div class="new-vendor-fields">
              <IntercomInput
                v-model="newVendor.name"
                label="Name"
                placeholder="Vendor name"
                :disabled="isCreatingVendor"
              />
              <IntercomPhoneInput
                v-model="newVendor.phone"
                label="Phone"
                :disabled="isCreatingVendor"
                @update:valid="(v) => (newVendorPhoneValid = v)"
              />
            </div>
            <div class="new-vendor-actions">
              <button type="button" class="cancel-inline-btn" @click="cancelNewVendor">
                Cancel
              </button>
              <IntercomButton
                variant="primary"
                size="sm"
                :loading="isCreatingVendor"
                :disabled="!newVendor.name || (newVendor.phone && !newVendorPhoneValid)"
                @click="createAndSelectVendor"
              >
                Create & Select
              </IntercomButton>
            </div>
          </div>
        </div>

        <div class="form-field">
          <IntercomInput
            ref="vendorBillNumberRef"
            v-model="formData.vendor_bill_number"
            label="Vendor Bill Number"
            placeholder="Vendor's own invoice number (optional)"
            @keydown.enter.prevent="advance('vendorBillNumber')"
          />
        </div>
      </div>

      <!-- Bill Date & Due Date -->
      <div class="form-row">
        <div class="form-field">
          <IntercomDateInput
            v-model="formData.bill_date"
            label="Bill Date"
            @keydown.enter.prevent="advance('billDate')"
          />
        </div>

        <div v-if="billingSettings.show_due_date !== false" class="form-field">
          <IntercomDateInput
            v-model="formData.due_date"
            label="Due Date"
            :min="formData.bill_date || undefined"
            @keydown.enter.prevent="advance('dueDate')"
          />
        </div>
      </div>

      <!-- Notes -->
      <div class="form-row">
        <div class="form-field" style="grid-column: 1 / -1;">
          <IntercomInput
            v-model="formData.notes"
            label="Notes"
            placeholder="Optional notes"
            type="textarea"
            :rows="2"
          />
        </div>
      </div>

      <!-- Edit Reason (finalized bills only) -->
      <div v-if="isFinalizedEdit" class="form-row">
        <div class="form-field" style="grid-column: 1 / -1;">
          <IntercomInput
            v-model="formData.edit_reason"
            label="Edit Reason"
            placeholder="Why are you editing this finalized bill?"
            type="textarea"
            :rows="2"
          />
          <p class="finalized-edit-hint">This bill is finalized. Stock quantities will be automatically adjusted.</p>
        </div>
      </div>

      <!-- Line Items Section -->
      <div class="section-divider">
        <span class="section-title">Line Items</span>
      </div>

      <!-- Line Items Table -->
      <div class="line-items-table">
        <div class="line-items-header">
          <div class="col-product">Product</div>
          <div class="col-hsn">HSN</div>
          <div class="col-qty">Qty</div>
          <div class="col-price">Base Price</div>
          <div class="col-disc">Disc</div>
          <div class="col-gst">GST%</div>
          <div class="col-total">Total</div>
          <div class="col-actions"></div>
        </div>

        <div
          v-for="(item, index) in formData.items"
          :key="index"
          class="line-item-row"
        >
          <div class="col-product">
            <IntercomSelect
              :ref="(el) => setItemRef(index, 'product', el)"
              v-model="item.product_id"
              :options="productOptions"
              placeholder="Type to search products..."
              filterable
              @update:model-value="(v) => handleProductSelect(index, v)"
              @search="handleProductSearch"
              @keydown.enter="(e) => { if (!e.defaultPrevented) { e.preventDefault(); handleItemEnter(index, 'product') } }"
            />
          </div>

          <div class="col-hsn">
            <span class="readonly-display">{{ item.hsn_code || '-' }}</span>
          </div>

          <div class="col-qty">
            <input
              :ref="(el) => setItemRef(index, 'qty', el)"
              v-model.number="item.quantity"
              type="number"
              min="0.01"
              step="0.01"
              class="item-input"
              placeholder="Qty"
              @keydown.enter.prevent="handleItemEnter(index, 'qty')"
              @keydown.left="handleItemArrowLeft(index, 'qty', $event)"
              @keydown.right="handleItemArrowRight(index, 'qty', $event)"
            />
          </div>

          <div class="col-price">
            <input
              :ref="(el) => setItemRef(index, 'price', el)"
              v-model.number="item.unit_price"
              type="number"
              min="0"
              step="0.01"
              class="item-input"
              placeholder="Price"
              @keydown.enter.prevent="handleItemEnter(index, 'price')"
              @keydown.left="handleItemArrowLeft(index, 'price', $event)"
              @keydown.right="handleItemArrowRight(index, 'price', $event)"
            />
          </div>

          <div class="col-disc">
            <div class="disc-input-wrap">
              <input
                :ref="(el) => setItemRef(index, 'discount', el)"
                v-model.number="item.discount_value"
                type="number"
                min="0"
                step="0.01"
                class="item-input disc-input"
                placeholder="0"
                @keydown.enter.prevent="handleItemEnter(index, 'discount')"
                @keydown.left="handleItemArrowLeft(index, 'discount', $event)"
                @keydown.right="handleItemArrowRight(index, 'discount', $event)"
              />
              <button
                type="button"
                class="disc-toggle"
                :title="item.discount_type === 'percentage' ? 'Percentage' : 'Flat amount'"
                @click="toggleDiscountType(index)"
              >{{ item.discount_type === 'percentage' ? '%' : '&#8377;' }}</button>
            </div>
          </div>

          <div class="col-gst">
            <span class="readonly-display">{{ item.is_taxable ? (item.igst_rate > 0 ? item.igst_rate : item.cgst_rate + item.sgst_rate) + '%' : '-' }}</span>
          </div>

          <div class="col-total">
            <span class="total-display">&#8377;{{ calculateItemTotal(item).toFixed(2) }}</span>
          </div>

          <div class="col-actions">
            <button
              type="button"
              class="remove-btn"
              :disabled="formData.items.length === 1"
              @click="removeLineItem(index)"
            >
              &#10005;
            </button>
          </div>
        </div>

        <button type="button" class="add-item-btn" @click="addLineItem">
          + Add Item
        </button>
      </div>

      <!-- Bill-Level Discount -->
      <div class="bill-discount-section">
        <span class="bill-discount-label">Bill Discount:</span>
        <div class="bill-discount-input">
          <input
            ref="billDiscountRef"
            v-model.number="formData.discount_value"
            type="number"
            min="0"
            step="0.01"
            class="item-input"
            placeholder="0"
            @keydown.enter.prevent="advance('billDiscount')"
          />
          <button
            type="button"
            class="disc-toggle"
            @click="toggleBillDiscountType"
          >{{ formData.discount_type === 'percentage' ? '%' : '&#8377;' }}</button>
        </div>
      </div>

      <!-- Totals Section -->
      <div class="totals-section">
        <div class="totals-row">
          <span class="totals-label">Subtotal:</span>
          <span class="totals-value">&#8377;{{ billTotals.subtotal.toFixed(2) }}</span>
        </div>
        <div v-if="billTotals.itemDiscount > 0" class="totals-row discount-row">
          <span class="totals-label">Item Discounts:</span>
          <span class="totals-value">-&#8377;{{ billTotals.itemDiscount.toFixed(2) }}</span>
        </div>
        <div v-if="billTotals.billDiscount > 0" class="totals-row discount-row">
          <span class="totals-label">Bill Discount:</span>
          <span class="totals-value">-&#8377;{{ billTotals.billDiscount.toFixed(2) }}</span>
        </div>
        <div class="totals-row">
          <span class="totals-label">Taxable Amount:</span>
          <span class="totals-value">&#8377;{{ billTotals.taxable.toFixed(2) }}</span>
        </div>
        <div v-if="billTotals.cgst > 0" class="totals-row">
          <span class="totals-label">CGST:</span>
          <span class="totals-value cgst">&#8377;{{ billTotals.cgst.toFixed(2) }}</span>
        </div>
        <div v-if="billTotals.sgst > 0" class="totals-row">
          <span class="totals-label">SGST:</span>
          <span class="totals-value sgst">&#8377;{{ billTotals.sgst.toFixed(2) }}</span>
        </div>
        <div v-if="billTotals.igst > 0" class="totals-row">
          <span class="totals-label">IGST:</span>
          <span class="totals-value igst">&#8377;{{ billTotals.igst.toFixed(2) }}</span>
        </div>
        <div class="totals-row grand-total">
          <span class="totals-label">Grand Total:</span>
          <span class="totals-value">&#8377;{{ billTotals.total.toFixed(2) }}</span>
        </div>
      </div>
    </div>

    <template #footer>
      <IntercomButton variant="secondary" @click="handleCancel">
        Cancel
      </IntercomButton>
      <IntercomButton
        v-if="!isEditMode"
        variant="secondary"
        :loading="isSaving"
        :disabled="!canSubmit"
        @click="handleSubmit(false)"
      >
        Save as Draft
      </IntercomButton>
      <IntercomButton
        variant="primary"
        :loading="isSaving"
        :disabled="!canSubmit"
        @click="handleSubmit(true)"
      >
        {{ isEditMode ? 'Update Purchase Bill' : 'Save & Finalize' }}
      </IntercomButton>
    </template>
  </IntercomModal>
</template>

<script setup>
import { ref, reactive, watch, computed, nextTick } from 'vue'
import { useToast } from '@shared/composables/useToast.js'
import { useFormKeyboard } from '@shared/composables/useFormKeyboard.js'
import { IntercomModal, IntercomButton, IntercomInput, IntercomDateInput, IntercomPhoneInput, IntercomSelect } from '@shared/components/intercom'
import { useBilling } from '../composables/useBilling.js'

const props = defineProps({
  show: Boolean,
  bill: { type: Object, default: null },
})

const emit = defineEmits(['update:show', 'success'])

const toast = useToast()
const { listVendors, createVendor, listProducts, createPurchaseBill, updatePurchaseBill, finalizePurchaseBill, getBillingSettings } = useBilling()

// Billing settings (controls due date visibility)
const billingSettings = ref({})

// Edit mode
const isEditMode = computed(() => !!props.bill)
const isFinalizedEdit = computed(() => isEditMode.value && props.bill?.status === 'finalized')

// State
const showModal = ref(props.show)
const isLoadingVendors = ref(false)
const isLoadingProducts = ref(false)
const isSaving = ref(false)
const vendorOptions = ref([])
const productOptions = ref([])
const allProducts = ref([])

// --- Keyboard navigation ---
const vendorSelectRef = ref(null)
const vendorBillNumberRef = ref(null)
const billDateRef = ref(null)
const dueDateRef = ref(null)
const billDiscountRef = ref(null)

// Refs for line item fields: itemRefs[index] = { product, qty, price, discount }
const itemRefs = ref({})
const setItemRef = (index, field, el) => {
  if (!itemRefs.value[index]) itemRefs.value[index] = {}
  itemRefs.value[index][field] = el
}

const { field: kbField, advance, moveLeft, moveRight, handleEscape, clearFields, focusFirst } = useFormKeyboard({
  onSubmit: () => { if (canSubmit.value) handleSubmit(true) },
  onEscape: () => { showModal.value = false },
})

// Re-register fields when items change
const registerKeyboardFields = () => {
  clearFields()
  kbField('vendor', vendorSelectRef)
  kbField('vendorBillNumber', vendorBillNumberRef)
  kbField('billDate', billDateRef)
  if (billingSettings.value?.show_due_date !== false) {
    kbField('dueDate', dueDateRef)
  }
  // Line items
  formData.items.forEach((_, i) => {
    const refs = itemRefs.value[i] || {}
    kbField(`item-${i}-product`, ref(refs.product), { type: 'select' })
    kbField(`item-${i}-qty`, ref(refs.qty), { type: 'number', row: `item-${i}` })
    kbField(`item-${i}-price`, ref(refs.price), { type: 'number', row: `item-${i}` })
    kbField(`item-${i}-discount`, ref(refs.discount), { type: 'number', row: `item-${i}` })
  })
  kbField('billDiscount', billDiscountRef, { type: 'number' })
}

// Handle Enter on IntercomSelect: advance when dropdown is closed (event not prevented)
const onSelectEnter = (fieldName, e) => {
  if (!e.defaultPrevented) {
    e.preventDefault()
    advance(fieldName)
  }
}

// Helper for line item Enter/Arrow handlers
const handleItemEnter = (index, field) => advance(`item-${index}-${field}`)
const handleItemArrowLeft = (index, field, e) => moveLeft(`item-${index}-${field}`, e)
const handleItemArrowRight = (index, field, e) => moveRight(`item-${index}-${field}`, e)

// Vendor select options with "+ New Vendor" sentinel at end
const vendorSelectOptions = computed(() => {
  return [
    ...vendorOptions.value,
    { value: '__create_new__', label: '+ New Vendor', isCreateNew: true },
  ]
})

// Inline new vendor form
const showNewVendorForm = ref(false)
const isCreatingVendor = ref(false)
const newVendor = reactive({ name: '', phone: '' })
const newVendorPhoneValid = ref(true)

// Default item template
const defaultItem = () => ({
  product_id: null,
  quantity: 1,
  unit_price: 0,
  hsn_code: null,
  is_taxable: false,
  cgst_rate: 0,
  sgst_rate: 0,
  igst_rate: 0,
  discount_type: 'percentage',
  discount_value: 0,
})

// Today's date for default bill_date
const todayISO = new Date().toISOString().slice(0, 10)

// Form data
const formData = reactive({
  vendor_id: null,
  vendor_bill_number: '',
  bill_date: todayISO,
  due_date: '',
  notes: '',
  discount_type: 'fixed',
  discount_value: 0,
  items: [defaultItem()],
  edit_reason: '',
})

// Computed: Can submit form
const canSubmit = computed(() => {
  if (!formData.vendor_id) return false
  if (formData.items.length === 0) return false
  return formData.items.every((item) => {
    return item.product_id && item.quantity > 0 && item.unit_price > 0
  })
})

// Computed: Bill totals — GST-exclusive (vendor prices are base prices, tax added on top)
// Discount is subtracted from the base price before GST is calculated.
const billTotals = computed(() => {
  let subtotal = 0
  let totalItemDiscount = 0
  let cgst = 0
  let sgst = 0
  let igst = 0

  // Per-item: compute line base, item discount, then taxable amount
  const itemTaxables = formData.items.map((item) => {
    const lineBase = (item.quantity || 0) * (item.unit_price || 0)
    let discount = 0
    if (item.discount_value > 0) {
      if (item.discount_type === 'percentage') {
        discount = lineBase * (Math.min(item.discount_value, 100) / 100)
      } else {
        discount = Math.min(item.discount_value, lineBase)
      }
    }
    totalItemDiscount += discount
    const taxableAmount = lineBase - discount
    subtotal += lineBase
    return { taxableAmount, item }
  })

  const netSubtotal = subtotal - totalItemDiscount

  // Bill-level discount (applied to net subtotal after item discounts)
  let billDisc = 0
  if (formData.discount_value > 0) {
    if (formData.discount_type === 'percentage') {
      billDisc = netSubtotal * (Math.min(formData.discount_value, 100) / 100)
    } else {
      billDisc = Math.min(formData.discount_value, netSubtotal)
    }
  }

  const taxableTotal = netSubtotal - billDisc

  // Compute tax proportionally per item
  itemTaxables.forEach(({ taxableAmount, item }) => {
    if (!item.is_taxable || netSubtotal === 0) return
    // Proportionally adjust for bill discount
    const proportion = taxableAmount / netSubtotal
    const adjustedTaxable = taxableAmount - (billDisc * proportion)

    if (item.cgst_rate > 0) cgst += adjustedTaxable * item.cgst_rate / 100
    if (item.sgst_rate > 0) sgst += adjustedTaxable * item.sgst_rate / 100
    if (item.igst_rate > 0) igst += adjustedTaxable * item.igst_rate / 100
  })

  const totalTax = cgst + sgst + igst
  return {
    subtotal,
    itemDiscount: totalItemDiscount,
    billDiscount: billDisc,
    taxable: taxableTotal,
    cgst,
    sgst,
    igst,
    total: taxableTotal + totalTax,
  }
})

// Watch show prop
watch(
  () => props.show,
  async (val) => {
    showModal.value = val
    if (val) {
      // Fetch billing settings (due date visibility)
      try {
        const s = await getBillingSettings()
        billingSettings.value = s?.data || s || {}
      } catch { /* defaults apply */ }
      fetchVendors()
      await fetchProducts()
      if (props.bill) {
        populateFromBill(props.bill)
      } else {
        resetForm()
      }
    }
  }
)

// Watch modal state — focus first field when modal opens
watch(showModal, (val) => {
  emit('update:show', val)
  if (val) {
    nextTick(() => focusFirst())
  }
})

// Re-register keyboard fields when line items change
watch(
  () => formData.items.length,
  () => nextTick(registerKeyboardFields),
  { immediate: true }
)

/**
 * Fetch vendors for dropdown
 */
const fetchVendors = async () => {
  isLoadingVendors.value = true
  try {
    const response = await listVendors({ limit: 100 })
    vendorOptions.value = (response.data || []).map((v) => ({
      label: v.name,
      value: v.id,
    }))
  } catch (error) {
    console.error('Failed to fetch vendors:', error)
    vendorOptions.value = []
  } finally {
    isLoadingVendors.value = false
  }
}

/**
 * Fetch products for dropdown
 */
const fetchProducts = async () => {
  isLoadingProducts.value = true
  try {
    const response = await listProducts({ limit: 100 })
    allProducts.value = response.data || []
    productOptions.value = allProducts.value.map((p) => ({
      label: `${p.name} - \u20B9${p.base_price}`,
      value: p.id,
      product: p,
    }))
  } catch (error) {
    console.error('Failed to fetch products:', error)
    productOptions.value = []
  } finally {
    isLoadingProducts.value = false
  }
}

/**
 * Server-side vendor search. Debounced. Empty/short query restores the
 * default top-100 list. Keeps the selected vendor visible even if absent
 * from search results.
 */
let vendorSearchTimer = null
const handleVendorSearch = (query) => {
  if (vendorSearchTimer) clearTimeout(vendorSearchTimer)
  if (!query || query.length < 2) {
    fetchVendors()
    return
  }
  vendorSearchTimer = setTimeout(async () => {
    try {
      const response = await listVendors({ search: query, limit: 20 })
      const results = response.data || []
      const toOpt = (v) => ({ label: v.name, value: v.id })
      const selectedId = formData.vendor_id
      const resultIds = new Set(results.map((r) => r.id))
      const selectedOption = selectedId && !resultIds.has(selectedId)
        ? vendorOptions.value.find((o) => o.value === selectedId)
        : null
      vendorOptions.value = [
        ...(selectedOption ? [selectedOption] : []),
        ...results.map(toOpt),
      ]
    } catch (error) {
      console.error('Vendor search failed:', error)
    }
  }, 250)
}

/**
 * Server-side product search
 */
let productSearchTimer = null
const handleProductSearch = (query) => {
  if (productSearchTimer) clearTimeout(productSearchTimer)
  if (!query || query.length < 2) {
    productOptions.value = allProducts.value.map((p) => ({
      label: `${p.name} - \u20B9${p.base_price}`,
      value: p.id,
      product: p,
    }))
    return
  }
  productSearchTimer = setTimeout(async () => {
    try {
      const response = await listProducts({ search: query, limit: 20 })
      const results = response.data || []
      const existingIds = new Set(allProducts.value.map(p => p.id))
      for (const r of results) {
        if (!existingIds.has(r.id)) {
          allProducts.value.push(r)
        }
      }
      const toOpt = (p) => ({
        label: `${p.name} - \u20B9${p.base_price}`,
        value: p.id,
        product: p,
      })
      // Always include products already selected in line items so their
      // labels keep rendering while the user searches in another row
      const selectedIds = new Set(
        formData.items.map(i => i.product_id).filter(Boolean)
      )
      const resultIds = new Set(results.map(r => r.id))
      const selectedOptions = allProducts.value
        .filter(p => selectedIds.has(p.id) && !resultIds.has(p.id))
        .map(toOpt)
      productOptions.value = [...selectedOptions, ...results.map(toOpt)]
    } catch (error) {
      console.error('Product search failed:', error)
    }
  }, 250)
}

/**
 * Handle vendor select change — intercept the sentinel value
 */
const handleVendorSelectChange = (value) => {
  if (value === '__create_new__') {
    nextTick(() => {
      formData.vendor_id = null
    })
    showNewVendorForm.value = true
  }
}

/**
 * Cancel inline vendor creation
 */
const cancelNewVendor = () => {
  showNewVendorForm.value = false
  Object.assign(newVendor, { name: '', phone: '' })
}

/**
 * Create new vendor inline and auto-select them
 */
const createAndSelectVendor = async () => {
  if (!newVendor.name.trim()) {
    toast.warning('Vendor name is required')
    return
  }
  if (newVendor.phone && !newVendorPhoneValid.value) {
    toast.warning('Enter a valid 10-digit phone number')
    return
  }
  isCreatingVendor.value = true
  try {
    const created = await createVendor({
      name: newVendor.name.trim(),
      phone: newVendor.phone || null,
    })
    const vendor = created?.vendor || created

    // Add to dropdown and auto-select
    vendorOptions.value.unshift({ label: vendor.name, value: vendor.id })
    formData.vendor_id = vendor.id

    // Hide form and reset
    showNewVendorForm.value = false
    Object.assign(newVendor, { name: '', phone: '' })

    toast.success('Vendor created')
  } catch (error) {
    console.error('Failed to create vendor:', error)
    toast.error('Failed to create vendor')
  } finally {
    isCreatingVendor.value = false
  }
}

/**
 * Handle product selection — auto-fill unit_price from product.base_price
 */
const handleProductSelect = (index, productId) => {
  const item = formData.items[index]
  const pid = productId ?? item.product_id
  if (!pid) return

  const product = allProducts.value.find((p) => p.id === pid)
  if (!product) return

  // cost_price is the vendor's base price (exclusive of GST) — correct for GST-exclusive purchase billing.
  // base_price is the retail selling price (GST-inclusive MRP) — used only as fallback if cost_price is unset.
  item.unit_price = product.cost_price || product.base_price
  item.hsn_code = product.hsn_code || null
  item.is_taxable = product.is_taxable || false
  // For display only — show the intra-state rate (CGST+SGST).
  // Backend determines the actual split (intra vs inter-state) based on vendor state code.
  item.cgst_rate = product.is_taxable ? (product.cgst_rate ?? 0) : 0
  item.sgst_rate = product.is_taxable ? (product.sgst_rate ?? 0) : 0
  item.igst_rate = 0 // Never set both CGST+SGST and IGST — backend decides based on vendor state
}

/**
 * Calculate item total (after item-level discount)
 */
const calculateItemTotal = (item) => {
  const lineBase = (item.quantity || 0) * (item.unit_price || 0)
  let discount = 0
  if (item.discount_value > 0) {
    if (item.discount_type === 'percentage') {
      discount = lineBase * (Math.min(item.discount_value, 100) / 100)
    } else {
      discount = Math.min(item.discount_value, lineBase)
    }
  }
  return lineBase - discount
}

/**
 * Toggle item-level discount type between percentage and fixed
 */
const toggleDiscountType = (index) => {
  const item = formData.items[index]
  item.discount_type = item.discount_type === 'percentage' ? 'fixed' : 'percentage'
  item.discount_value = 0
}

/**
 * Toggle bill-level discount type
 */
const toggleBillDiscountType = () => {
  formData.discount_type = formData.discount_type === 'percentage' ? 'fixed' : 'percentage'
  formData.discount_value = 0
}

/**
 * Add new line item
 */
const addLineItem = () => {
  formData.items.push(defaultItem())
}

/**
 * Remove line item
 */
const removeLineItem = (index) => {
  if (formData.items.length > 1) {
    formData.items.splice(index, 1)
  }
}

/**
 * Populate form from existing bill for editing
 */
const populateFromBill = (bill) => {
  showNewVendorForm.value = false
  Object.assign(newVendor, { name: '', phone: '' })
  formData.vendor_id = bill.vendor_id
  formData.vendor_bill_number = bill.vendor_bill_number || ''
  formData.bill_date = bill.bill_date ? new Date(bill.bill_date).toISOString().slice(0, 10) : todayISO
  formData.due_date = bill.due_date ? new Date(bill.due_date).toISOString().slice(0, 10) : ''
  formData.notes = bill.notes || ''
  formData.discount_type = bill.discount_type || 'fixed'
  formData.discount_value = bill.discount_value || 0
  formData.items = (bill.items || []).map((item) => ({
    product_id: item.product_id,
    quantity: parseFloat(Number(item.quantity).toFixed(3)),
    unit_price: item.unit_price,
    hsn_code: item.hsn_code || null,
    is_taxable: !!(item.cgst_rate || item.sgst_rate || item.igst_rate),
    cgst_rate: item.cgst_rate || 0,
    sgst_rate: item.sgst_rate || 0,
    igst_rate: item.igst_rate || 0,
    discount_type: item.discount_type || 'percentage',
    discount_value: item.discount_value || 0,
  }))
  if (formData.items.length === 0) {
    formData.items = [defaultItem()]
  }

  // Inject bill's products into options if they weren't in the paginated fetch
  for (const item of (bill.items || [])) {
    if (!item.product_id) continue
    const exists = allProducts.value.some((p) => p.id === item.product_id)
    if (!exists) {
      const product = {
        id: item.product_id,
        name: item.product_name || 'Unknown Product',
        base_price: item.unit_price,
        cost_price: item.unit_price,
        hsn_code: item.hsn_code,
        is_taxable: !!(item.cgst_rate || item.sgst_rate || item.igst_rate),
        cgst_rate: item.cgst_rate || 0,
        sgst_rate: item.sgst_rate || 0,
      }
      allProducts.value.push(product)
      productOptions.value.push({
        label: `${product.name} - \u20B9${product.base_price}`,
        value: product.id,
        product,
      })
    }
  }
}

/**
 * Reset form
 */
const resetForm = () => {
  showNewVendorForm.value = false
  Object.assign(newVendor, { name: '', phone: '' })
  formData.vendor_id = null
  formData.vendor_bill_number = ''
  formData.bill_date = todayISO
  formData.due_date = ''
  formData.notes = ''
  formData.discount_type = 'fixed'
  formData.discount_value = 0
  formData.items = [defaultItem()]
  formData.edit_reason = ''
}

/**
 * Handle cancel
 */
const handleCancel = () => {
  showModal.value = false
  resetForm()
}

/**
 * Handle submit — optionally finalize immediately after creation
 */
const handleSubmit = async (andFinalize = false) => {
  if (!canSubmit.value) return

  isSaving.value = true
  try {
    const billData = {
      vendor_id: formData.vendor_id,
      vendor_bill_number: formData.vendor_bill_number || null,
      bill_date: formData.bill_date || null,
      due_date: formData.due_date || null,
      notes: formData.notes || null,
      discount_type: formData.discount_value > 0 ? formData.discount_type : null,
      discount_value: formData.discount_value || 0,
      items: formData.items.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        cgst_rate: item.is_taxable ? item.cgst_rate : 0,
        sgst_rate: item.is_taxable ? item.sgst_rate : 0,
        igst_rate: item.is_taxable ? item.igst_rate : 0,
        discount_type: item.discount_value > 0 ? item.discount_type : null,
        discount_value: item.discount_value || 0,
      })),
    }

    if (isEditMode.value) {
      if (isFinalizedEdit.value) {
        billData.edit_reason = formData.edit_reason || null
      }
      await updatePurchaseBill(props.bill.id, billData)
      toast.success(isFinalizedEdit.value ? 'Finalized purchase bill updated — stock adjusted' : 'Purchase bill updated')
    } else {
      const createResponse = await createPurchaseBill(billData)

      // Finalize immediately if requested (increments stock)
      if (andFinalize) {
        const billId = createResponse.purchase_bill?.id || createResponse.data?.id || createResponse.id
        if (billId) {
          await finalizePurchaseBill(billId)
          toast.success('Purchase bill created and finalized — stock updated')
        }
      }
    }

    showModal.value = false
    resetForm()
    emit('success')
  } catch (error) {
    const action = isEditMode.value ? 'update' : 'create'
    console.error(`Failed to ${action} purchase bill:`, error)
    toast.error(`Failed to ${action} purchase bill: ` + (error.message || 'Unknown error'))
  } finally {
    isSaving.value = false
  }
}
</script>

<style scoped>
.purchase-bill-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.form-input {
  width: 100%;
  height: 44px;
  padding: 0 var(--space-3);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  background: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  outline: none;
  transition: all var(--transition-fast);
}

.form-input:focus {
  background: var(--color-white);
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1);
}

.section-divider {
  position: relative;
  text-align: center;
  margin: var(--space-2) 0;
}

.section-divider::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
  background: var(--color-gray-200);
}

.section-title {
  position: relative;
  display: inline-block;
  padding: 0 var(--space-3);
  background: var(--color-white);
  color: var(--color-gray-600);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.line-items-table {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.line-items-header,
.line-item-row {
  display: grid;
  grid-template-columns: 1fr 80px 70px 90px 90px 55px 90px 36px;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-2) var(--space-3);
}

.line-items-header {
  background: var(--color-gray-100);
  border-radius: var(--radius-md);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.line-item-row {
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-gray-200);
}

/* Prevent long product names from breaking grid */
.col-product {
  min-width: 0;
  overflow: hidden;
}

.col-product :deep(.n-base-selection-label) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-hsn,
.col-gst {
  text-align: center;
}

.col-total {
  text-align: right;
}

.item-input {
  width: 100%;
  height: 36px;
  padding: 0 var(--space-2);
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-sm);
  outline: none;
  transition: all var(--transition-fast);
}

.item-input:focus {
  border-color: var(--color-blue);
  box-shadow: 0 0 0 2px rgba(0, 87, 255, 0.1);
}

.item-input[type='number'] {
  text-align: right;
}

/* Discount input with toggle */
.disc-input-wrap {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: var(--color-white);
}

.disc-input {
  border: none !important;
  border-radius: 0 !important;
  height: 32px !important;
  width: 50px;
  min-width: 0;
}

.disc-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  background: var(--color-gray-100);
  border: none;
  border-left: 1px solid var(--color-gray-200);
  cursor: pointer;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-600);
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.disc-toggle:hover {
  background: var(--color-gray-200);
  color: var(--color-charcoal);
}

/* Bill-level discount */
.bill-discount-section {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-3);
  margin-bottom: var(--space-3);
}

.bill-discount-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
}

.bill-discount-input {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: var(--color-white);
  width: 140px;
}

.bill-discount-input .item-input {
  border: none !important;
  height: 36px;
}

.bill-discount-input .disc-toggle {
  height: 36px;
}

/* Discount row in totals */
.totals-row.discount-row .totals-value {
  color: var(--color-green-600, #16a34a);
}

.total-display {
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  font-weight: var(--font-weight-medium);
}

.readonly-display {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

.totals-value.cgst { color: #2563eb; }
.totals-value.sgst { color: #7c3aed; }
.totals-value.igst { color: #0891b2; }

.remove-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--color-gray-500);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-size: 16px;
}

.remove-btn:hover:not(:disabled) {
  background: #fee;
  color: #d03050;
}

.remove-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.add-item-btn {
  align-self: flex-start;
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-blue);
  background: transparent;
  border: 1px dashed var(--color-gray-300);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.add-item-btn:hover {
  background: rgba(0, 87, 255, 0.05);
  border-color: var(--color-blue);
}

.totals-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  margin-left: auto;
  min-width: 280px;
  max-width: 320px;
}

.totals-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-2) 0;
}

.totals-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  font-weight: var(--font-weight-medium);
}

.totals-value {
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  font-weight: var(--font-weight-semibold);
}

.totals-row.grand-total {
  border-top: 2px solid var(--color-gray-300);
  padding-top: var(--space-3);
  margin-top: var(--space-2);
}

.totals-row.grand-total .totals-label {
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  font-weight: var(--font-weight-bold);
}

.totals-row.grand-total .totals-value {
  font-size: var(--font-size-lg);
  color: var(--color-blue);
  font-weight: var(--font-weight-bold);
}

/* Sentinel "+ New Vendor" option in IntercomSelect dropdown */
.add-new-option {
  color: var(--color-blue);
  font-weight: var(--font-weight-medium);
}

.new-vendor-form {
  margin-top: var(--space-3);
  padding: var(--space-4);
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: var(--radius-lg);
}

.new-vendor-header {
  margin-bottom: var(--space-3);
}

.new-vendor-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: #1d4ed8;
}

.new-vendor-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
  margin-bottom: var(--space-3);
}

.new-vendor-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  padding-top: var(--space-3);
  border-top: 1px solid #bfdbfe;
}

.cancel-inline-btn {
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  background: transparent;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.cancel-inline-btn:hover {
  background: var(--color-gray-50);
}

@media (max-width: 640px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .new-vendor-fields {
    grid-template-columns: 1fr;
  }
}

.finalized-edit-hint {
  margin-top: var(--space-1);
  font-size: var(--text-xs);
  color: var(--color-warning-600, #d97706);
}
</style>
